// slices/pinCodeSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import PinCodeService from "../services/PinCodeService";

// ==================== HELPER: Error Handler ====================
const handleError = (error) => {
  console.log('Full error object:', JSON.stringify(error, null, 2));
  
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  // Check if error is already a string
  if (typeof error === 'string') {
    errorMessage = error;
  }
  // Check if error has data property (your API response structure)
  else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  }
  // Check if error has direct error property
  else if (error?.error && typeof error.error === 'string') {
    errorMessage = error.error;
  }
  // Check if error has message property
  else if (error?.message) {
    errorMessage = error.message;
  }
  // Check response structure
  else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message;
  }
  else if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    errorMessage = error.response.data.error;
  }
  else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  }
  else if (error?.response?.data?.data?.error?.message) {
    errorMessage = error.response.data.data.error.message;
  }
  // Check if error has success: false structure
  else if (error?.success === false && error?.error) {
    errorMessage = error.error;
  }
  // Check if error has statusCode and data
  else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  }
  // Check if error has response but no data
  else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  }
  // Check if error is an object with error property
  else if (error?.error) {
    if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error.message) {
      errorMessage = error.error.message;
    }
  }
  
  console.log('Extracted error message:', errorMessage);
  return errorMessage;
};

// ==================== ASYNC THUNKS ====================

// -------- PIN GENERATION & CREATION --------

export const generatePin = createAsyncThunk(
  'pinCode/generatePin',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PinCodeService.generatePin();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const createPin = createAsyncThunk(
  'pinCode/createPin',
  async (expiryMinutes = 15, { rejectWithValue }) => {
    try {
      const response = await PinCodeService.createPin(expiryMinutes);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- PIN SENDING --------

export const sendPin = createAsyncThunk(
  'pinCode/sendPin',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PinCodeService.sendPin();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const markPinSent = createAsyncThunk(
  'pinCode/markPinSent',
  async (method, { rejectWithValue }) => {
    try {
      const response = await PinCodeService.markPinSent(method);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- PIN VERIFICATION --------

export const verifyPin = createAsyncThunk(
  'pinCode/verifyPin',
  async (pin, { rejectWithValue }) => {
    try {
      const response = await PinCodeService.verifyPin(pin);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const verifyPinAndGetUser = createAsyncThunk(
  'pinCode/verifyPinAndGetUser',
  async (pin, { rejectWithValue }) => {
    try {
      const response = await PinCodeService.verifyPinAndGetUser(pin);
      console.log('verify result', response);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- PIN STATUS CHECKS --------

export const isValidPin = createAsyncThunk(
  'pinCode/isValidPin',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PinCodeService.isValidPin();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const isPinVerified = createAsyncThunk(
  'pinCode/isPinVerified',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PinCodeService.isPinVerified();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const getPinStatus = createAsyncThunk(
  'pinCode/getPinStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PinCodeService.getPinStatus();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- PIN MANAGEMENT --------

export const resetPin = createAsyncThunk(
  'pinCode/resetPin',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PinCodeService.resetPin();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const regeneratePin = createAsyncThunk(
  'pinCode/regeneratePin',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PinCodeService.regeneratePin();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const maskPhoneNumber = createAsyncThunk(
  'pinCode/maskPhoneNumber',
  async (phone, { rejectWithValue }) => {
    try {
      const response = await PinCodeService.maskPhoneNumber(phone);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== SLICE ====================

const initialState = {
  // PIN data
  pin: null,
  pinStatus: null,
  isPinValid: false,
  isPinVerified: false,
  
  // Verification
  verified: false,
  emailVerified: false,
  phoneVerified: false,
  
  // PIN status details
  pinExists: false,
  pinExpired: false,
  pinLocked: false,
  attemptsUsed: 0,
  attemptsRemaining: 5,
  expiresAt: null,
  lockedUntil: null,
  
  // Sending status
  sentToEmail: false,
  sentToPhone: false,
  
  // User data after verification
  verifiedUser: null,
  
  // Loading states
  loading: false,
  sending: false,
  verifying: false,
  
  // Error states
  error: null,
  sendError: null,
  verifyError: null,
  
  // Success states
  sendSuccess: false,
  verifySuccess: false,
  
  // UI states
  pinInput: '',
  attempts: 0,
  isLocked: false,
  lockTimer: null
};

const pinCodeSlice = createSlice({
  name: 'pinCode',
  initialState,
  reducers: {
    resetPinState: (state) => {
      state.pin = null;
      state.pinStatus = null;
      state.isPinValid = false;
      state.isPinVerified = false;
      state.verified = false;
      state.emailVerified = false;
      state.phoneVerified = false;
      state.pinExists = false;
      state.pinExpired = false;
      state.pinLocked = false;
      state.attemptsUsed = 0;
      state.attemptsRemaining = 5;
      state.expiresAt = null;
      state.lockedUntil = null;
      state.sentToEmail = false;
      state.sentToPhone = false;
      state.verifiedUser = null;
      state.error = null;
      state.sendError = null;
      state.verifyError = null;
      state.sendSuccess = false;
      state.verifySuccess = false;
      state.pinInput = '';
      state.attempts = 0;
      state.isLocked = false;
      state.lockTimer = null;
    },
    
    resetSendState: (state) => {
      state.sendSuccess = false;
      state.sendError = null;
      state.sentToEmail = false;
      state.sentToPhone = false;
    },
    
    resetVerifyState: (state) => {
      state.verifySuccess = false;
      state.verifyError = null;
      state.verified = false;
      state.emailVerified = false;
      state.phoneVerified = false;
      state.verifiedUser = null;
    },
    
    clearPinErrors: (state) => {
      state.error = null;
      state.sendError = null;
      state.verifyError = null;
    },
    
    setPinInput: (state, action) => {
      state.pinInput = action.payload;
    },
    
    clearPinInput: (state) => {
      state.pinInput = '';
    },
    
    incrementAttempts: (state) => {
      state.attempts += 1;
      state.attemptsRemaining = Math.max(0, 5 - state.attempts);
      if (state.attempts >= 5) {
        state.isLocked = true;
        state.pinLocked = true;
        state.lockedUntil = new Date(Date.now() + 30 * 60 * 1000);
      }
    },
    
    resetAttempts: (state) => {
      state.attempts = 0;
      state.attemptsRemaining = 5;
      state.isLocked = false;
      state.pinLocked = false;
      state.lockedUntil = null;
    },
    
    setLockTimer: (state, action) => {
      state.lockTimer = action.payload;
    },
    
    clearLockTimer: (state) => {
      state.lockTimer = null;
    },
    
    setVerified: (state, action) => {
      state.verified = action.payload;
      state.isPinVerified = action.payload;
    },
    
    updatePinStatus: (state, action) => {
      const status = action.payload;
      if (status) {
        state.pinExists = status.exists || false;
        state.isPinVerified = status.verified || false;
        state.pinExpired = status.expired || false;
        state.pinLocked = status.locked || false;
        state.attemptsUsed = status.attemptsUsed || 0;
        state.attemptsRemaining = status.attemptsRemaining || 0;
        state.expiresAt = status.expiresAt || null;
        state.lockedUntil = status.lockedUntil || null;
        state.sentToEmail = status.sentToEmail || false;
        state.sentToPhone = status.sentToPhone || false;
        state.emailVerified = status.emailVerified || false;
        state.phoneVerified = status.phoneVerified || false;
        state.isPinValid = status.exists && !status.expired && !status.locked && !status.verified;
        state.verified = status.verified || false;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // ========== GENERATE PIN ==========
      .addCase(generatePin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generatePin.fulfilled, (state, action) => {
        state.loading = false;
        state.pin = action.payload.data?.pin || null;
        state.pinExists = true;
      })
      .addCase(generatePin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to generate PIN';
      })
      
      // ========== CREATE PIN ==========
      .addCase(createPin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPin.fulfilled, (state, action) => {
        state.loading = false;
        state.pin = action.payload.data?.pin || null;
        state.pinExists = true;
      })
      .addCase(createPin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create PIN';
      })
      
      // ========== SEND PIN ==========
      .addCase(sendPin.pending, (state) => {
        state.sending = true;
        state.sendError = null;
        state.sendSuccess = false;
      })
      .addCase(sendPin.fulfilled, (state, action) => {
        state.sending = false;
        state.sendSuccess = true;
        if (action.payload.data) {
          state.sentToEmail = action.payload.data.sentToEmail || false;
          state.sentToPhone = action.payload.data.sentToPhone || false;
        }
      })
      .addCase(sendPin.rejected, (state, action) => {
        state.sending = false;
        state.sendError = action.payload || 'Failed to send PIN';
        state.sendSuccess = false;
      })
      
      // ========== MARK PIN SENT ==========
      .addCase(markPinSent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markPinSent.fulfilled, (state, action) => {
        state.loading = false;
        const method = action.meta.arg;
        if (method === 'email') {
          state.sentToEmail = true;
        } else if (method === 'phone') {
          state.sentToPhone = true;
        }
      })
      .addCase(markPinSent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to mark PIN as sent';
      })
      
      // ========== VERIFY PIN ==========
      .addCase(verifyPin.pending, (state) => {
        state.verifying = true;
        state.verifyError = null;
        state.verifySuccess = false;
      })
      .addCase(verifyPin.fulfilled, (state, action) => {
        state.verifying = false;
        state.verifySuccess = true;
        state.verified = true;
        state.isPinVerified = true;
        state.emailVerified = action.payload.data?.emailVerified || false;
        state.phoneVerified = action.payload.data?.phoneVerified || false;
        state.pinExists = false;
        state.pin = null;
        state.isPinValid = false;
        state.attempts = 0;
        state.attemptsRemaining = 5;
        state.isLocked = false;
        state.pinLocked = false;
      })
      .addCase(verifyPin.rejected, (state, action) => {
        state.verifying = false;
        state.verifyError = action.payload || 'Failed to verify PIN';
        state.verifySuccess = false;
        state.attempts += 1;
        state.attemptsRemaining = Math.max(0, 5 - state.attempts);
        if (state.attempts >= 5) {
          state.isLocked = true;
          state.pinLocked = true;
          state.lockedUntil = new Date(Date.now() + 30 * 60 * 1000);
        }
      })
      
      // ========== VERIFY PIN AND GET USER ==========
      .addCase(verifyPinAndGetUser.pending, (state) => {
        state.verifying = true;
        state.verifyError = null;
        state.verifySuccess = false;
      })
      .addCase(verifyPinAndGetUser.fulfilled, (state, action) => {
        state.verifying = false;
        state.verifySuccess = true;
        state.verified = true;
        state.isPinVerified = true;
        
        // ✅ FIXED: Get user data from the response
        const userData = action.payload?.data?.user || action.payload?.user;
        
        if (userData) {
          state.verifiedUser = userData;
          state.emailVerified = userData.emailVerified || false;
          state.phoneVerified = userData.phoneVerified || false;
          
          console.log('✅ Redux state updated:', {
            verifySuccess: state.verifySuccess,
            phoneVerified: state.phoneVerified,
            emailVerified: state.emailVerified,
            hasUser: !!state.verifiedUser
          });
        }
        
        state.pinExists = false;
        state.pin = null;
        state.isPinValid = false;
        state.attempts = 0;
        state.attemptsRemaining = 5;
        state.isLocked = false;
        state.pinLocked = false;
      })
      .addCase(verifyPinAndGetUser.rejected, (state, action) => {
        state.verifying = false;
        state.verifyError = action.payload || 'Failed to verify PIN';
        state.verifySuccess = false;
        state.verified = false;
      })
      
      // ========== IS VALID PIN ==========
      .addCase(isValidPin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(isValidPin.fulfilled, (state, action) => {
        state.loading = false;
        state.isPinValid = action.payload.data?.isValid || false;
      })
      .addCase(isValidPin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to check PIN validity';
      })
      
      // ========== IS PIN VERIFIED ==========
      .addCase(isPinVerified.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(isPinVerified.fulfilled, (state, action) => {
        state.loading = false;
        state.isPinVerified = action.payload.data?.isVerified || false;
        state.verified = state.isPinVerified;
      })
      .addCase(isPinVerified.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to check PIN verification';
      })
      
      // ========== GET PIN STATUS ==========
      .addCase(getPinStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPinStatus.fulfilled, (state, action) => {
        state.loading = false;
        const status = action.payload.data;
        if (status) {
          state.pinStatus = status;
          state.pinExists = status.exists || false;
          state.isPinVerified = status.verified || false;
          state.pinExpired = status.expired || false;
          state.pinLocked = status.locked || false;
          state.attemptsUsed = status.attemptsUsed || 0;
          state.attemptsRemaining = status.attemptsRemaining || 0;
          state.expiresAt = status.expiresAt || null;
          state.lockedUntil = status.lockedUntil || null;
          state.sentToEmail = status.sentToEmail || false;
          state.sentToPhone = status.sentToPhone || false;
          state.emailVerified = status.emailVerified || false;
          state.phoneVerified = status.phoneVerified || false;
          state.isPinValid = status.exists && !status.expired && !status.locked && !status.verified;
          state.verified = status.verified || false;
        }
      })
      .addCase(getPinStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get PIN status';
      })
      
      // ========== RESET PIN ==========
      .addCase(resetPin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPin.fulfilled, (state) => {
        state.loading = false;
        state.pin = null;
        state.pinExists = false;
        state.isPinValid = false;
        state.isPinVerified = false;
        state.verified = false;
        state.pinExpired = false;
        state.pinLocked = false;
        state.attemptsUsed = 0;
        state.attemptsRemaining = 5;
        state.expiresAt = null;
        state.lockedUntil = null;
        state.sentToEmail = false;
        state.sentToPhone = false;
        state.verifiedUser = null;
        state.attempts = 0;
        state.isLocked = false;
        state.lockTimer = null;
      })
      .addCase(resetPin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to reset PIN';
      })
      
      // ========== REGENERATE PIN ==========
      .addCase(regeneratePin.pending, (state) => {
        state.sending = true;
        state.sendError = null;
        state.sendSuccess = false;
      })
      .addCase(regeneratePin.fulfilled, (state, action) => {
        state.sending = false;
        state.sendSuccess = true;
        state.pinExists = true;
        state.isPinValid = true;
        state.isPinVerified = false;
        state.verified = false;
        state.pinExpired = false;
        state.pinLocked = false;
        state.attempts = 0;
        state.attemptsRemaining = 5;
        state.isLocked = false;
        if (action.payload.data) {
          state.sentToEmail = action.payload.data.sentToEmail || false;
          state.sentToPhone = action.payload.data.sentToPhone || false;
        }
      })
      .addCase(regeneratePin.rejected, (state, action) => {
        state.sending = false;
        state.sendError = action.payload || 'Failed to regenerate PIN';
        state.sendSuccess = false;
      });
  }
});

// ==================== SELECTORS ====================

const selectPinState = (state) => state.pinCode;

// PIN data selectors
export const selectPin = createSelector(
  selectPinState,
  (pin) => pin.pin
);

export const selectPinStatus = createSelector(
  selectPinState,
  (pin) => pin.pinStatus
);

export const selectIsPinValid = createSelector(
  selectPinState,
  (pin) => pin.isPinValid
);

export const selectIsPinVerified = createSelector(
  selectPinState,
  (pin) => pin.isPinVerified
);

export const selectVerified = createSelector(
  selectPinState,
  (pin) => pin.verified
);

export const selectEmailVerified = createSelector(
  selectPinState,
  (pin) => pin.emailVerified
);

export const selectPhoneVerified = createSelector(
  selectPinState,
  (pin) => pin.phoneVerified
);

// PIN status details selectors
export const selectPinExists = createSelector(
  selectPinState,
  (pin) => pin.pinExists
);

export const selectPinExpired = createSelector(
  selectPinState,
  (pin) => pin.pinExpired
);

export const selectPinLocked = createSelector(
  selectPinState,
  (pin) => pin.pinLocked
);

export const selectAttemptsUsed = createSelector(
  selectPinState,
  (pin) => pin.attemptsUsed
);

export const selectAttemptsRemaining = createSelector(
  selectPinState,
  (pin) => pin.attemptsRemaining
);

export const selectExpiresAt = createSelector(
  selectPinState,
  (pin) => pin.expiresAt
);

export const selectLockedUntil = createSelector(
  selectPinState,
  (pin) => pin.lockedUntil
);

// Sending status selectors
export const selectSentToEmail = createSelector(
  selectPinState,
  (pin) => pin.sentToEmail
);

export const selectSentToPhone = createSelector(
  selectPinState,
  (pin) => pin.sentToPhone
);

// User data selectors
export const selectVerifiedUser = createSelector(
  selectPinState,
  (pin) => pin.verifiedUser
);

// Loading selectors
export const selectPinLoading = createSelector(
  selectPinState,
  (pin) => pin.loading
);

export const selectPinSending = createSelector(
  selectPinState,
  (pin) => pin.sending
);

export const selectPinVerifying = createSelector(
  selectPinState,
  (pin) => pin.verifying
);

// Error selectors
export const selectPinError = createSelector(
  selectPinState,
  (pin) => pin.error
);

export const selectPinSendError = createSelector(
  selectPinState,
  (pin) => pin.sendError
);

export const selectPinVerifyError = createSelector(
  selectPinState,
  (pin) => pin.verifyError
);

// Success selectors
export const selectPinSendSuccess = createSelector(
  selectPinState,
  (pin) => pin.sendSuccess
);

export const selectPinVerifySuccess = createSelector(
  selectPinState,
  (pin) => pin.verifySuccess
);

// UI selectors
export const selectPinInput = createSelector(
  selectPinState,
  (pin) => pin.pinInput
);

export const selectPinAttempts = createSelector(
  selectPinState,
  (pin) => pin.attempts
);

export const selectIsLocked = createSelector(
  selectPinState,
  (pin) => pin.isLocked
);

export const selectLockTimer = createSelector(
  selectPinState,
  (pin) => pin.lockTimer
);

// Derived selectors
export const selectCanAttemptPin = createSelector(
  [selectIsPinValid, selectIsPinVerified, selectPinLocked, selectAttemptsRemaining],
  (isValid, isVerified, isLocked, attemptsRemaining) => {
    return isValid && !isVerified && !isLocked && attemptsRemaining > 0;
  }
);

export const selectPinStatusMessage = createSelector(
  [selectPinExists, selectIsPinVerified, selectPinExpired, selectPinLocked, selectAttemptsRemaining],
  (exists, verified, expired, locked, attemptsRemaining) => {
    if (verified) return '✅ PIN already verified';
    if (locked) return '🔒 PIN is locked. Please try again later.';
    if (expired) return '⏰ PIN has expired. Please request a new one.';
    if (!exists) return '📱 No PIN found. Please request a new PIN.';
    if (attemptsRemaining === 0) return '❌ Too many failed attempts. PIN is locked.';
    if (attemptsRemaining > 0) {
      return `📝 Enter your PIN (${attemptsRemaining} attempts remaining)`;
    }
    return '📱 Please enter your PIN';
  }
);

export const selectIsFullyVerified = createSelector(
  [selectEmailVerified, selectPhoneVerified],
  (emailVerified, phoneVerified) => emailVerified && phoneVerified
);

// ==================== EXPORTS ====================

export const {
  resetPinState,
  resetSendState,
  resetVerifyState,
  clearPinErrors,
  setPinInput,
  clearPinInput,
  incrementAttempts,
  resetAttempts,
  setLockTimer,
  clearLockTimer,
  setVerified,
  updatePinStatus
} = pinCodeSlice.actions;

export default pinCodeSlice.reducer;